import { FC } from "react";
import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Colors } from "../../../configs";
import {
  IReadyDateRule,
  ReadyDay,
} from "../../../configs/readyDateTable";

const CLOSED = "CLOSED";

const DAY_OPTIONS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const label = (day: string) => day.charAt(0) + day.slice(1).toLowerCase();

type RuleField = Exclude<keyof IReadyDateRule, "dropOffDay">;

const COLUMNS: { field: RuleField; cutoff: string; service: string }[] = [
  { field: "beforeCutoffSpecial", cutoff: "Before Cutoff", service: "Special" },
  { field: "beforeCutoffRegular", cutoff: "Before Cutoff", service: "Regular" },
  { field: "afterCutoffSpecial", cutoff: "After Cutoff", service: "Special" },
  { field: "afterCutoffRegular", cutoff: "After Cutoff", service: "Regular" },
];

export interface IReadyDateTableEditorProps {
  rules: IReadyDateRule[];
  onChange: (rules: IReadyDateRule[]) => void;
  /** Read-only (e.g. while the weekly table mode is off) */
  disabled?: boolean;
}

/**
 * Weekly ready-date table (reported issues 3.c): for each drop-off day, the
 * weekday a Special / Regular ticket is ready before and after the cut-off.
 */
export const ReadyDateTableEditor: FC<IReadyDateTableEditorProps> = ({
  rules,
  onChange,
  disabled = false,
}) => {
  const headSx = {
    fontSize: "var(--fs-xs)",
    fontWeight: 600,
    color: Colors.WHITE,
    background: Colors.SECONDARY,
    textAlign: "center" as const,
    py: "0.6rem",
    border: `1px solid ${Colors.WHITE}`,
  };
  const cellSx = { fontSize: "var(--fs-2xs)", py: "0.4rem", px: "0.6rem" };

  const update = (dropOffDay: string, field: RuleField, value: ReadyDay) =>
    onChange(
      rules.map((r) => (r.dropOffDay === dropOffDay ? { ...r, [field]: value } : r))
    );

  return (
    <Table size="small" aria-label="Ready date table">
      <TableHead>
        <TableRow>
          <TableCell rowSpan={2} sx={headSx}>
            Drop-off Date
          </TableCell>
          <TableCell colSpan={2} sx={headSx}>
            Before Cutoff
          </TableCell>
          <TableCell colSpan={2} sx={headSx}>
            After Cutoff
          </TableCell>
        </TableRow>
        <TableRow>
          {COLUMNS.map((c) => (
            <TableCell key={c.field} sx={headSx}>
              {c.service}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {DAY_OPTIONS.map((day) => {
          const rule = rules.find((r) => r.dropOffDay === day);
          if (!rule) return null;
          return (
            <TableRow key={day} data-testid={`ready-date-row-${day}`}>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{label(day)}</TableCell>
              {COLUMNS.map((c) => (
                <TableCell key={c.field} sx={cellSx}>
                  <Select
                    size="small"
                    fullWidth
                    value={rule[c.field] ?? CLOSED}
                    disabled={disabled}
                    onChange={(e) =>
                      update(day, c.field, e.target.value === CLOSED ? null : e.target.value)
                    }
                    SelectDisplayProps={{
                      "aria-label": `${label(day)} ${c.cutoff} ${c.service}`,
                    } as React.HTMLAttributes<HTMLDivElement>}
                    sx={{ fontSize: "var(--fs-2xs)" }}
                  >
                    {DAY_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d} sx={{ fontSize: "var(--fs-2xs)" }}>
                        {label(d)}
                      </MenuItem>
                    ))}
                    <MenuItem value={CLOSED} sx={{ fontSize: "var(--fs-2xs)" }}>
                      Closed
                    </MenuItem>
                  </Select>
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
